/* immerkind theme – global runtime (vanilla web components, no dependencies).
   Drawers (dialog), two-level menu drawer, mega menu, quantity stepper,
   sticky bar, tabs, cart count helpers, money formatting. */
(() => {
  const ik = (window.ikTheme = window.ikTheme || {});

  /* ---------------------------------------------------------------- utils */

  ik.debounce = (fn, wait = 200) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  ik.fetchConfig = (type = 'json') => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
  });

  ik.formatMoney = (cents, format) => {
    const moneyFormat = format || ik.moneyFormat || '{{amount}}';
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = moneyFormat.match(placeholderRegex);
    const key = match ? match[1] : 'amount';

    const formatWithDelimiters = (number, precision, thousands, decimal) => {
      if (Number.isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const integer = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const fraction = parts[1] ? decimal + parts[1] : '';
      return integer + fraction;
    };

    let value;
    switch (key) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
    }
    return moneyFormat.replace(placeholderRegex, value);
  };

  ik.getFocusable = (container) =>
    Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);

  let lockCount = 0;
  ik.lockScroll = (lock) => {
    lockCount = Math.max(0, lockCount + (lock ? 1 : -1));
    document.body.classList.toggle('is-locked', lockCount > 0);
  };

  ik.announce = (message) => {
    let region = document.getElementById('ik-live-region');
    if (!region) {
      region = document.createElement('div');
      region.id = 'ik-live-region';
      region.className = 'sr-status';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 50);
  };

  ik.cart = ik.cart || {};
  ik.cart.setCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
    document.querySelectorAll('[data-cart-icon]').forEach((el) => {
      const strings = ik.strings || {};
      const label = count > 0 ? (strings.cartWithCount || 'Warenkorb, [count] Artikel').replace('[count]', count) : strings.cartEmpty || 'Warenkorb, leer';
      el.setAttribute('aria-label', label);
    });
  };

  ik.replaceSection = (sectionId, html, selector) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const source = selector ? doc.querySelector(selector) : doc.querySelector(`#shopify-section-${sectionId}`);
    const target = selector ? document.querySelector(selector) : document.querySelector(`#shopify-section-${sectionId}`);
    if (source && target) target.innerHTML = source.innerHTML;
    return !!(source && target);
  };

  /* --------------------------------------------------------------- drawer */

  /* One delegated listener for all [data-drawer-open] triggers – survives re-rendered markup. */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (drawer && typeof drawer.open === 'function') {
      event.preventDefault();
      drawer.open(opener);
    }
  });

  class IkDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('close', () => this.afterClose());
    }

    get openers() {
      return Array.from(document.querySelectorAll(`[data-drawer-open="${this.id}"]`));
    }

    get isOpen() {
      return !!(this.dialog && this.dialog.open);
    }

    open(opener) {
      if (!this.dialog || this.dialog.open) return;
      this.opener = opener || document.activeElement;
      this.dialog.showModal();
      ik.lockScroll(true);
      this.openers.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
      const focusTarget = this.dialog.querySelector('[autofocus]') || this.dialog.querySelector('[data-drawer-focus]') || this.dialog.querySelector('h2, h1') || ik.getFocusable(this.dialog)[0];
      if (focusTarget) {
        if (!focusTarget.hasAttribute('tabindex') && !/^(a|button|input|select|textarea)$/i.test(focusTarget.tagName)) focusTarget.setAttribute('tabindex', '-1');
        window.requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
      }
      this.dispatchEvent(new CustomEvent('ik:drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.dialog || !this.dialog.open) return;
      this.dialog.close();
    }

    afterClose() {
      ik.lockScroll(false);
      this.openers.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      if (this.opener && typeof this.opener.focus === 'function') this.opener.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('ik:drawer:close', { bubbles: true }));
    }
  }

  class IkMenuDrawer extends IkDrawer {
    connectedCallback() {
      super.connectedCallback();
      this.panels = Array.from(this.querySelectorAll('[data-panel]'));
      this.querySelectorAll('[data-panel-open]').forEach((btn) => {
        btn.addEventListener('click', () => this.showPanel(btn.dataset.panelOpen, btn));
      });
      this.querySelectorAll('[data-panel-back]').forEach((btn) => {
        btn.addEventListener('click', () => this.showPanel('root'));
      });
    }

    showPanel(id, trigger) {
      this.panels.forEach((panel) => {
        const active = panel.dataset.panel === id;
        panel.hidden = !active;
        if (active) {
          const focusTarget = panel.querySelector('[data-panel-back]') || panel.querySelector('button, a');
          if (focusTarget) window.requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
        }
      });
      this.querySelectorAll('[data-panel-open]').forEach((btn) => btn.setAttribute('aria-expanded', btn === trigger ? 'true' : 'false'));
      if (id === 'root' && this.lastTrigger) this.lastTrigger.focus({ preventScroll: true });
      this.lastTrigger = trigger || null;
    }

    afterClose() {
      super.afterClose();
      this.showPanel('root');
    }
  }

  /* ------------------------------------------------------------ mega menu */

  class IkMegaMenu extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-nav-item]')).filter((item) => item.querySelector('[data-mega-panel]'));
      this.overlay = document.querySelector('[data-mega-overlay]');
      this.timers = new Map();
      this.openItem = null;

      this.items.forEach((item) => {
        const trigger = item.querySelector('[data-mega-trigger]');
        const panel = item.querySelector('[data-mega-panel]');
        item.addEventListener('mouseenter', () => this.schedule(item, true));
        item.addEventListener('mouseleave', () => this.schedule(item, false));
        trigger.addEventListener('click', (event) => {
          if (this.openItem !== item) {
            event.preventDefault();
            this.clearTimer(item);
            this.open(item);
          }
        });
        trigger.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.open(item);
            const first = panel.querySelector('a');
            if (first) first.focus();
          }
        });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.openItem) {
          const trigger = this.openItem.querySelector('[data-mega-trigger]');
          this.close(this.openItem);
          trigger.focus();
        }
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.closeAll();
      });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.closeAll();
      });
      if (this.overlay) this.overlay.addEventListener('click', () => this.closeAll());
    }

    clearTimer(item) {
      clearTimeout(this.timers.get(item));
    }

    schedule(item, shouldOpen) {
      this.clearTimer(item);
      this.timers.set(
        item,
        setTimeout(() => (shouldOpen ? this.open(item) : this.close(item)), 300)
      );
    }

    open(item) {
      if (this.openItem && this.openItem !== item) this.close(this.openItem);
      item.querySelector('[data-mega-trigger]').setAttribute('aria-expanded', 'true');
      item.querySelector('[data-mega-panel]').hidden = false;
      this.openItem = item;
      if (this.overlay) this.overlay.hidden = false;
    }

    close(item) {
      if (!item) return;
      item.querySelector('[data-mega-trigger]').setAttribute('aria-expanded', 'false');
      item.querySelector('[data-mega-panel]').hidden = true;
      if (this.openItem === item) this.openItem = null;
      if (!this.openItem && this.overlay) this.overlay.hidden = true;
    }

    closeAll() {
      this.items.forEach((item) => this.clearTimer(item));
      if (this.openItem) this.close(this.openItem);
    }
  }

  /* --------------------------------------------------------------- header */

  class IkHeader extends HTMLElement {
    connectedCallback() {
      const sticky = this.classList.contains('is-sticky');
      const wrapper = this.closest('.shopify-section');
      if (wrapper) wrapper.classList.toggle('shopify-section--header-sticky', sticky);
      if (!sticky) return;
      const update = () => {
        this.classList.toggle('is-stuck', window.scrollY > this.offsetHeight);
      };
      window.addEventListener('scroll', update, { passive: true });
      update();
    }
  }

  /* ------------------------------------------------------------- quantity */

  class IkQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.minus = this.querySelector('[data-quantity-change="minus"]');
      this.plus = this.querySelector('[data-quantity-change="plus"]');
      if (!this.input) return;
      [this.minus, this.plus].forEach((btn) => {
        if (!btn) return;
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          const step = btn === this.minus ? -1 : 1;
          const previous = Number(this.input.value) || 0;
          const next = Math.min(this.max, Math.max(this.min, previous + step));
          if (next !== previous) {
            this.input.value = next;
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          this.update();
        });
      });
      this.input.addEventListener('change', () => this.update());
      this.update();
    }

    get min() {
      return Number(this.input.min || 0);
    }

    get max() {
      return this.input.max ? Number(this.input.max) : Infinity;
    }

    update() {
      const value = Number(this.input.value) || 0;
      if (this.minus) this.minus.disabled = value <= this.min;
      if (this.plus) this.plus.disabled = value >= this.max;
    }
  }

  /* ----------------------------------------------------------- sticky bar */

  class IkStickyBar extends HTMLElement {
    connectedCallback() {
      const target = this.dataset.target ? document.querySelector(this.dataset.target) : null;
      if (!target || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        ([entry]) => {
          const scrolledPast = entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', !entry.isIntersecting && scrolledPast);
        },
        { threshold: 0 }
      );
      this.observer.observe(target);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  /* ----------------------------------------------------------------- tabs */

  class IkTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (!this.tabs.length) return;
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', (event) => {
          event.preventDefault();
          this.select(index);
        });
        tab.addEventListener('keydown', (event) => {
          let next = null;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (next !== null) {
            event.preventDefault();
            this.select(next);
            this.tabs[next].focus();
          }
        });
      });
      const initial = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      this.select(initial >= 0 ? initial : 0, false);
    }

    select(index, scroll = true) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !active;
      });
      if (scroll && this.dataset.scrollTo === 'panel') {
        const panel = document.getElementById(this.tabs[index].getAttribute('aria-controls'));
        if (panel) panel.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }
  }

  /* ------------------------------------------------------- newsletter etc. */

  class IkNewsletterForm extends HTMLElement {
    connectedCallback() {
      const form = this.querySelector('form');
      const input = this.querySelector('input[type="email"]');
      const error = this.querySelector('[data-form-error]');
      if (!form || !input) return;
      form.addEventListener('submit', (event) => {
        if (!input.checkValidity()) {
          event.preventDefault();
          input.setAttribute('aria-invalid', 'true');
          if (error) error.hidden = false;
          input.focus();
        }
      });
      input.addEventListener('input', () => {
        input.removeAttribute('aria-invalid');
        if (error) error.hidden = true;
      });
    }
  }

  /* Mobile section navigation rendered as a <select> (service pages). */
  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-nav-select]') && event.target.value) window.location.href = event.target.value;
  });

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };
  define('ik-drawer', IkDrawer);
  define('ik-menu-drawer', IkMenuDrawer);
  define('ik-mega-menu', IkMegaMenu);
  define('ik-header', IkHeader);
  define('ik-quantity', IkQuantity);
  define('ik-sticky-bar', IkStickyBar);
  define('ik-tabs', IkTabs);
  define('ik-newsletter-form', IkNewsletterForm);

  ik.IkDrawer = IkDrawer;
})();
